// Crew Mode Charter D-4 shared identity mechanism — client-side session
// state. Tracks which operator is "active" on this device after a
// successful PIN entry, and PIN-attempt lockout to prevent misattribution
// from a fat-fingered guess landing on a colleague's PIN. Everything here
// is local-only (tenant-scoped localStorage) and works fully offline —
// verifying the PIN itself (against the cached operator roster) is a
// separate concern, see operatorPinCrypto.ts and operatorRoster.ts.

import { getActiveTenantKey, getTenantScopedKey } from '../offline/tenant'

export type OperatorSession = {
  actorId: string
  displayName: string
  tenantMembershipId: string
  startedAt: string
  expiresAt: string
}

const SESSION_STORAGE_BASE_KEY = 'crewModeOperatorSession'
const ATTEMPTS_STORAGE_BASE_KEY = 'crewModeOperatorPinAttempts'

// Charter D-4 mechanism decision (Control Tower, 2026-09-04): 8-hour
// session, matching a typical farm shift, or an explicit operator switch.
export const OPERATOR_SESSION_DURATION_MS = 8 * 60 * 60 * 1000

// Not a security control (see operatorPinCrypto.ts) — this exists purely
// to stop repeated-guess misattribution, so the thresholds are generous.
export const MAX_FAILED_PIN_ATTEMPTS = 5
export const PIN_LOCKOUT_DURATION_MS = 60 * 1000

const hasStorage = (): boolean => typeof globalThis !== 'undefined' && Boolean(globalThis.window?.localStorage)

const readJSON = <T>(key: string): T | null => {
  if (!hasStorage()) return null
  try {
    const raw = globalThis.window?.localStorage?.getItem(key)
    if (!raw) return null
    return JSON.parse(raw) as T
  } catch {
    return null
  }
}

const writeJSON = (key: string, value: unknown): void => {
  if (!hasStorage()) return
  try {
    globalThis.window?.localStorage?.setItem(key, JSON.stringify(value))
  } catch {
    // ignore — session state is a convenience cache, not the source of truth
  }
}

const removeKey = (key: string): void => {
  if (!hasStorage()) return
  try {
    globalThis.window?.localStorage?.removeItem(key)
  } catch {
    // ignore
  }
}

/**
 * Returns the active operator session, or null if none is set or it has
 * expired. An expired session is cleared as a side effect.
 */
export const getCurrentOperatorSession = (
  tenantKey = getActiveTenantKey(),
  now: number = Date.now()
): OperatorSession | null => {
  const key = getTenantScopedKey(SESSION_STORAGE_BASE_KEY, tenantKey)
  const session = readJSON<OperatorSession>(key)
  if (!session) return null

  const expiresAtMs = Date.parse(session.expiresAt)
  if (!Number.isFinite(expiresAtMs) || expiresAtMs <= now) {
    removeKey(key)
    return null
  }

  return session
}

export const setCurrentOperatorSession = (
  args: { actorId: string; displayName: string; tenantMembershipId: string },
  tenantKey = getActiveTenantKey(),
  now: number = Date.now()
): OperatorSession => {
  const session: OperatorSession = {
    actorId: args.actorId,
    displayName: args.displayName,
    tenantMembershipId: args.tenantMembershipId,
    startedAt: new Date(now).toISOString(),
    expiresAt: new Date(now + OPERATOR_SESSION_DURATION_MS).toISOString()
  }
  writeJSON(getTenantScopedKey(SESSION_STORAGE_BASE_KEY, tenantKey), session)
  return session
}

export const clearCurrentOperatorSession = (tenantKey = getActiveTenantKey()): void => {
  removeKey(getTenantScopedKey(SESSION_STORAGE_BASE_KEY, tenantKey))
}

/**
 * Convenience for Crew Mode capture write paths: the `capturedByActorId` to
 * stamp onto a row at the moment of capture (Charter D-4), or undefined if
 * no operator session is active. Callers behind `OperatorGate` (see
 * src/screens/crewMode/OperatorGate.tsx) are guaranteed a session exists,
 * but this stays defensive for any capture path that might run outside it.
 */
export const getCurrentOperatorActorId = (tenantKey = getActiveTenantKey()): string | undefined => {
  return getCurrentOperatorSession(tenantKey)?.actorId
}

type PinAttemptState = {
  failedCount: number
  lockedUntil: string | null
}

/**
 * True while repeated failed PIN attempts have triggered a temporary
 * lockout. Not a security control — see MAX_FAILED_PIN_ATTEMPTS comment.
 */
export const isPinEntryLocked = (
  tenantKey = getActiveTenantKey(),
  now: number = Date.now()
): boolean => {
  const state = readJSON<PinAttemptState>(getTenantScopedKey(ATTEMPTS_STORAGE_BASE_KEY, tenantKey))
  if (!state?.lockedUntil) return false

  const lockedUntilMs = Date.parse(state.lockedUntil)
  return Number.isFinite(lockedUntilMs) && lockedUntilMs > now
}

export const recordFailedPinAttempt = (
  tenantKey = getActiveTenantKey(),
  now: number = Date.now()
): void => {
  const key = getTenantScopedKey(ATTEMPTS_STORAGE_BASE_KEY, tenantKey)
  const existing = readJSON<PinAttemptState>(key)
  const nextFailedCount = (existing?.failedCount ?? 0) + 1

  const nextState: PinAttemptState = {
    failedCount: nextFailedCount,
    lockedUntil: nextFailedCount >= MAX_FAILED_PIN_ATTEMPTS
      ? new Date(now + PIN_LOCKOUT_DURATION_MS).toISOString()
      : existing?.lockedUntil ?? null
  }
  writeJSON(key, nextState)
}

export const recordSuccessfulPinAttempt = (tenantKey = getActiveTenantKey()): void => {
  removeKey(getTenantScopedKey(ATTEMPTS_STORAGE_BASE_KEY, tenantKey))
}
